import { useQuery } from "@tanstack/react-query";

import {
  type ChatMessageMetadata,
  type MessageAgentReplyFeedbackVote,
  type MessageAgentReplyStatus,
} from "@fitness-app/data-models/entities/Chat";

import { pendingChatAgentSuggestionsQueryKey } from "~/modules/Chat/hooks/chatAgentSuggestionQueryKeys";
import { supabase, useAppSelector } from "~/store/initializeStore";

export type PendingChatAgentSuggestion = Omit<ChatMessageMetadata, "agentReplyStatus" | "agentReplyFeedbackVote"> & {
  agentReplyStatus: PendingChatAgentSuggestionStatus | null;
  agentReplyFeedbackVote: MessageAgentReplyFeedbackVote | null;
  trainee: null | {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    assignedTrainerId: string | null;
  };
  message: null | {
    authorId: string;
    content: string;
  };
};

export type PendingChatAgentSuggestionStatus = Exclude<MessageAgentReplyStatus, "sent_automatically">;

type PendingChatAgentSuggestionsResult = {
  messages: PendingChatAgentSuggestion[];
  count: number;
};

type UsePendingChatAgentSuggestionsParams = {
  enabled?: boolean;
  statuses?: PendingChatAgentSuggestionStatus[];
  selectedTrainers?: string[];
};

export const usePendingChatAgentSuggestions = ({
  enabled = true,
  statuses = ["suggested"],
  selectedTrainers = ["all"],
}: UsePendingChatAgentSuggestionsParams = {}) => {
  const userId = useAppSelector((store) => store.user.data?.id);
  const normalizedStatuses = statuses.length > 0 ? statuses : ["suggested"];

  return useQuery({
    queryKey: [...pendingChatAgentSuggestionsQueryKey, userId, ...normalizedStatuses, ...selectedTrainers],
    keepPreviousData: true,
    enabled: enabled && Boolean(userId),
    refetchInterval: enabled ? 15000 : false,
    queryFn: async (): Promise<PendingChatAgentSuggestionsResult> => {
      if (!userId) {
        return {
          messages: [],
          count: 0,
        };
      }

      let query = supabase
        .from("chat_message_metadata")
        .select(
          "*, message:chat_message(authorId, content), trainee:traineeId(firstName, lastName, email, id, assignedTrainerId)",
          { count: "exact" },
        )
        .in("agentReplyStatus", normalizedStatuses)
        .order("agentReplyGeneratedAt", { ascending: false })
        .order("createdAt", { ascending: false });

      if (selectedTrainers.length && selectedTrainers[0] !== "all") {
        query = query.in("agentReplyOwnerTeamMemberId", selectedTrainers);
      }

      const { data, error, count } = await query.returns<PendingChatAgentSuggestion[]>();

      if (error) {
        throw new Error(error.message);
      }

      return {
        messages: data || [],
        count: count ?? data?.length ?? 0,
      };
    },
  });
};
